import { useState, useEffect, Fragment } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card, Collapse } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { ivrApi, recordingsApi } from '../services/api'
import { DestinationSelector } from '../components/common'
import IVRFlowchart from '../components/common/IVRFlowchart'
import { useConfirm } from '../components/common/ConfirmDialog'

interface IVROption {
  id?: string
  digit: string
  action_type: string
  action_target: string
  // Nur für Nebenstellen: direkt rufen statt über den Dialplan der Nebenstelle
  direct_dial?: boolean
  direct_timeout?: number
}

interface IVRMenu {
  id: string
  name: string
  description: string
  greeting_recording_id: string
  timeout: number
  timeout_retries: number
  invalid_retries: number
  timeout_action_type: string
  timeout_action_target: string
  invalid_action_type: string
  invalid_action_target: string
  is_active: boolean
  options: IVROption[]
}

interface Recording {
  id: string
  name: string
}

const emptyForm = {
  name: '',
  description: '',
  greeting_recording_id: '',
  timeout: 10,
  timeout_retries: 3,
  invalid_retries: 3,
  timeout_action_type: '',
  timeout_action_target: '',
  invalid_action_type: '',
  invalid_action_target: '',
  is_active: true,
  options: [] as IVROption[]
}

const digits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#']

// Allowed destination types for IVR actions
// Zieltypen, die der IVR-Block in extensions.conf.j2 auch wirklich rendert
// (seit die IVR-Zweige dasselbe render_action-Makro benutzen wie die
// Zeitbedingungen). "Parken" und "Nachtmodus" fehlen bewusst: dafür gibt es
// im Makro keinen Zweig, sie würden still auflegen.
const IVR_ALLOWED_TYPES = [
  'extension',
  'ring_group',
  'queue',
  'ivr',
  'time_condition',
  'conference',
  'voicemail',
  'announcement',
  'recording',
  'external_dest',
  'external',
  'hangup',
]

// FastAPI liefert `detail` entweder als Text (HTTPException) oder als Liste von
// Validierungsfehlern. Bisher verschluckte die Seite beides und zeigte nur
// "Fehler beim Speichern" — ohne jeden Hinweis, welches Feld schuld war.
function apiErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { loc?: unknown[]; msg?: string }
    const field = Array.isArray(first.loc) ? first.loc.filter(p => p !== 'body').join('.') : ''
    return field ? `${field}: ${first.msg}` : (first.msg || fallback)
  }
  return fallback
}

export default function IVR() {
  const confirm = useConfirm()
  const [menus, setMenus] = useState<IVRMenu[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingMenu, setEditingMenu] = useState<IVRMenu | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [recordings, setRecordings] = useState<Recording[]>([])
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set())

  const toggleFlowchart = (menuId: string) => {
    const next = new Set(expandedMenus)
    if (next.has(menuId)) {
      next.delete(menuId)
    } else {
      next.add(menuId)
    }
    setExpandedMenus(next)
  }

  useEffect(() => {
    loadMenus()
    loadRecordings()
  }, [])

  const loadMenus = async () => {
    try {
      const response = await ivrApi.list()
      setMenus(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden der IVR-Menüs')
    } finally {
      setLoading(false)
    }
  }

  const loadRecordings = async () => {
    try {
      const response = await recordingsApi.list()
      setRecordings(response.data.items || [])
    } catch {
      console.error('Error loading recordings')
    }
  }

  const openCreateModal = () => {
    setEditingMenu(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (menu: IVRMenu) => {
    setEditingMenu(menu)
    setFormData({
      name: menu.name,
      description: menu.description || '',
      greeting_recording_id: menu.greeting_recording_id || '',
      timeout: menu.timeout || 10,
      timeout_retries: menu.timeout_retries || 3,
      invalid_retries: menu.invalid_retries || 3,
      timeout_action_type: menu.timeout_action_type || '',
      timeout_action_target: menu.timeout_action_target || '',
      invalid_action_type: menu.invalid_action_type || '',
      invalid_action_target: menu.invalid_action_target || '',
      is_active: menu.is_active,
      options: menu.options || []
    })
    setShowModal(true)
  }

  // Leere Auswahlfelder als null senden: '' ist weder eine UUID noch ein
  // gültiger Aktionstyp und liess das Speichern bisher mit 422 scheitern.
  const buildPayload = () => ({
    ...formData,
    greeting_recording_id: formData.greeting_recording_id || null,
    timeout_action_type: formData.timeout_action_type || null,
    timeout_action_target: formData.timeout_action_target || null,
    invalid_action_type: formData.invalid_action_type || null,
    invalid_action_target: formData.invalid_action_target || null,
    options: formData.options.map(o => ({
      digit: o.digit,
      action_type: o.action_type,
      action_target: o.action_target || null,
      // Direktwahl gibt es nur für Nebenstellen — bei anderen Zielen wieder
      // abräumen, damit ein Zielwechsel keine tote Einstellung zurücklässt.
      direct_dial: o.action_type?.toUpperCase() === 'EXTENSION' ? !!o.direct_dial : false,
      direct_timeout: o.direct_timeout ?? 20,
    })),
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const incomplete = formData.options.find(o => !o.action_type)
    if (incomplete) {
      toast.error(`Taste ${incomplete.digit}: bitte ein Ziel auswählen`)
      return
    }
    const duplicate = formData.options.find(
      (o, i) => formData.options.findIndex(x => x.digit === o.digit) !== i
    )
    if (duplicate) {
      toast.error(`Taste ${duplicate.digit} ist doppelt vergeben`)
      return
    }

    try {
      if (editingMenu) {
        await ivrApi.update(editingMenu.id, buildPayload())
        toast.success('IVR-Menü aktualisiert')
      } else {
        await ivrApi.create(buildPayload())
        toast.success('IVR-Menü erstellt')
      }
      closeModal()
      loadMenus()
    } catch (err) {
      toast.error(apiErrorMessage(err, editingMenu ? 'Fehler beim Aktualisieren' : 'Fehler beim Erstellen'))
    }
  }

  const handleDelete = async (menu: IVRMenu) => {
    const confirmed = await confirm({
      title: 'IVR-Menü löschen',
      message: `IVR-Menü "${menu.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await ivrApi.delete(menu.id)
      toast.success('IVR-Menü gelöscht')
      loadMenus()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingMenu(null)
    setFormData(emptyForm)
  }

  const addOption = () => {
    const usedDigits = formData.options.map(o => o.digit)
    const nextDigit = digits.find(d => !usedDigits.includes(d)) || '0'
    setFormData({
      ...formData,
      options: [...formData.options, { digit: nextDigit, action_type: '', action_target: '' }]
    })
  }

  const updateOptionDestination = (index: number, type: string, value: string) => {
    const newOptions = [...formData.options]
    newOptions[index] = {
      ...newOptions[index],
      action_type: type,
      action_target: value
    }
    setFormData({ ...formData, options: newOptions })
  }

  const updateOptionDigit = (index: number, digit: string) => {
    updateOption(index, { digit })
  }

  const updateOption = (index: number, patch: Partial<IVROption>) => {
    const newOptions = [...formData.options]
    newOptions[index] = { ...newOptions[index], ...patch }
    setFormData({ ...formData, options: newOptions })
  }

  const removeOption = (index: number) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index)
    })
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>IVR Menüs</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neues IVR-Menü
        </Button>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Beschreibung</th>
              <th>Optionen</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-4">Laden...</td></tr>
            ) : menus.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-4 text-muted">Keine IVR-Menüs vorhanden</td></tr>
            ) : (
              menus.map(menu => (
                <Fragment key={menu.id}>
                  <tr>
                    <td><strong>{menu.name}</strong></td>
                    <td>{menu.description || '-'}</td>
                    <td>
                      {(menu.options || []).map(opt => (
                        <Badge key={opt.digit} bg="secondary" className="me-1">{opt.digit}</Badge>
                      ))}
                    </td>
                    <td><Badge bg={menu.is_active ? 'success' : 'secondary'}>{menu.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                    <td className="text-end">
                      <Button
                        variant={expandedMenus.has(menu.id) ? 'primary' : 'outline-secondary'}
                        size="sm"
                        className="me-1"
                        title="Flowchart anzeigen"
                        onClick={() => toggleFlowchart(menu.id)}
                      >
                        <i className="bi bi-diagram-3"></i>
                      </Button>
                      <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(menu)}>
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(menu)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="p-0 border-0">
                      <Collapse in={expandedMenus.has(menu.id)}>
                        <div>
                          <IVRFlowchart menu={menu} />
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                </Fragment>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingMenu ? 'IVR-Menü bearbeiten' : 'Neues IVR-Menü'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="z.B. Hauptmenü"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ansage</Form.Label>
                  <Form.Select
                    value={formData.greeting_recording_id}
                    onChange={(e) => setFormData({...formData, greeting_recording_id: e.target.value})}
                  >
                    <option value="">-- Keine Ansage --</option>
                    {recordings.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Optionale Beschreibung"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Timeout (Sek.)</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.timeout}
                    onChange={(e) => setFormData({...formData, timeout: parseInt(e.target.value) || 10})}
                    min={1}
                    max={60}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ansage-Durchläufe ohne Eingabe</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.timeout_retries}
                    onChange={(e) => setFormData({...formData, timeout_retries: parseInt(e.target.value) || 0})}
                    min={0}
                    max={10}
                  />
                  <Form.Text className="text-muted">
                    Wie oft die Ansage läuft, wenn nichts getippt wird. 0 oder 1 = einmal,
                    dann sofort zum Timeout-Ziel.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ansage-Durchläufe bei Fehleingabe</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.invalid_retries}
                    onChange={(e) => setFormData({...formData, invalid_retries: parseInt(e.target.value) || 0})}
                    min={0}
                    max={10}
                  />
                  <Form.Text className="text-muted">
                    Dasselbe für eine nicht belegte Taste.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span><i className="bi bi-diagram-3 me-2"></i>Menü-Optionen</span>
                <Button variant="outline-primary" size="sm" onClick={addOption}>
                  <i className="bi bi-plus-lg me-1"></i> Option hinzufügen
                </Button>
              </Card.Header>
              <Card.Body>
                {formData.options.length === 0 ? (
                  <p className="text-muted mb-0">Keine Optionen definiert. Klicken Sie auf "Option hinzufügen".</p>
                ) : (
                  formData.options.map((option, index) => (
                    <Row key={index} className="mb-3 align-items-end pb-3 border-bottom">
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="small">Taste</Form.Label>
                          <Form.Select
                            value={option.digit}
                            onChange={(e) => updateOptionDigit(index, e.target.value)}
                          >
                            {digits.map(d => (
                              <option key={d} value={d}>{d}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={9}>
                        <Form.Group>
                          <Form.Label className="small">Ziel</Form.Label>
                          <DestinationSelector
                            destinationType={option.action_type}
                            destinationValue={option.action_target}
                            onChange={(type, value) => updateOptionDestination(index, type, value)}
                            allowedTypes={IVR_ALLOWED_TYPES}
                            excludeType="ivr"
                            excludeId={editingMenu?.id}
                            placeholder="Ziel suchen..."
                            size="sm"
                            allowExternal={true}
                          />
                        </Form.Group>
                      </Col>
                      {option.action_type?.toUpperCase() === 'EXTENSION' && (
                        <Col md={12} className="mt-2">
                          <div className="d-flex align-items-center gap-3 flex-wrap">
                            <Form.Check
                              type="checkbox"
                              id={`direct-${index}`}
                              label="Direkt zum Ziel"
                              checked={!!option.direct_dial}
                              onChange={(e) => updateOption(index, { direct_dial: e.target.checked })}
                            />
                            {option.direct_dial && (
                              <div className="d-flex align-items-center gap-2">
                                <span className="small text-muted">Klingeldauer</span>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  style={{ width: '90px' }}
                                  min={5}
                                  max={120}
                                  value={option.direct_timeout ?? 20}
                                  onChange={(e) => updateOption(index, { direct_timeout: parseInt(e.target.value) || 20 })}
                                />
                                <span className="small text-muted">Sek.</span>
                              </div>
                            )}
                          </div>
                          {option.direct_dial && (
                            <div className="small text-muted mt-1">
                              Die Nebenstelle wird direkt gerufen. Rufumleitung, Voicemail und
                              „nicht erreichbar"-Ziele der Nebenstelle greifen nicht — wird nicht
                              abgenommen (oder ist besetzt, offline oder auf Ruhe gestellt),
                              übernimmt das Timeout-Ziel dieses Menüs.
                            </div>
                          )}
                        </Col>
                      )}
                      <Col md={1}>
                        <Button variant="outline-danger" size="sm" onClick={() => removeOption(index)}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </Col>
                    </Row>
                  ))
                )}
              </Card.Body>
            </Card>

            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header className="small">
                    <i className="bi bi-clock me-2"></i>Bei Timeout
                  </Card.Header>
                  <Card.Body>
                    <DestinationSelector
                      destinationType={formData.timeout_action_type}
                      destinationValue={formData.timeout_action_target}
                      onChange={(type, value) => setFormData({
                        ...formData,
                        timeout_action_type: type,
                        timeout_action_target: value
                      })}
                      allowedTypes={IVR_ALLOWED_TYPES}
                      excludeType="ivr"
                      excludeId={editingMenu?.id}
                      placeholder="Ziel bei Timeout..."
                      allowExternal={true}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header className="small">
                    <i className="bi bi-exclamation-triangle me-2"></i>Bei ungültiger Eingabe
                  </Card.Header>
                  <Card.Body>
                    <DestinationSelector
                      destinationType={formData.invalid_action_type}
                      destinationValue={formData.invalid_action_target}
                      onChange={(type, value) => setFormData({
                        ...formData,
                        invalid_action_type: type,
                        invalid_action_target: value
                      })}
                      allowedTypes={IVR_ALLOWED_TYPES}
                      excludeType="ivr"
                      excludeId={editingMenu?.id}
                      placeholder="Ziel bei ungültiger Eingabe..."
                      allowExternal={true}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is_active"
                label="Aktiv"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingMenu ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
